"use client";

import { Suspense, useState, useTransition } from "react";
import { signInAction } from "@/server/signin-action";

function LoginForm() {
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") ?? "").trim();
    startTransition(async () => {
      const res = await signInAction(fd);
      if (res.ok) {
        setSent(email);
        return;
      }
      setError(res.error ?? "Sign in failed.");
    });
  }

  if (sent) {
    return (
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 space-y-4">
        <h1 className="text-2xl font-bold text-slate-900">Check your email</h1>
        <p className="text-sm text-slate-600">
          If an account exists for{" "}
          <span className="font-medium text-slate-800">{sent}</span>, we&apos;ve sent a
          sign-in link. It expires in 24 hours.
        </p>
        <p className="text-xs text-slate-500">
          Didn&apos;t get an email? Check spam, or{" "}
          <button
            type="button"
            onClick={() => setSent(null)}
            className="text-emerald-700 hover:underline"
          >
            try again
          </button>
          .
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 space-y-5"
    >
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Sign in</h1>
        <p className="text-sm text-slate-600 mt-1">
          We&apos;ll email you a secure link. No password needed.
        </p>
      </div>

      <label className="block">
        <span className="text-sm font-medium text-slate-700">Email</span>
        <input
          name="email"
          type="email"
          required
          autoComplete="email"
          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-emerald-500 outline-none"
        />
      </label>

      {error && <p className="text-sm text-rose-600">{error}</p>}

      <button
        type="submit"
        disabled={pending}
        className="w-full py-3 rounded-lg bg-emerald-600 text-white font-medium disabled:opacity-50 hover:bg-emerald-700"
      >
        {pending ? "Sending..." : "Send sign-in link"}
      </button>

      <p className="text-xs text-slate-500 text-center">
        Access is by invitation only. If you haven&apos;t been invited, contact Rachel
        directly.
      </p>
    </form>
  );
}

export default function LoginPage() {
  return (
    <main className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
      <Suspense>
        <LoginForm />
      </Suspense>
    </main>
  );
}
